import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyNoticeDraft,
    clearNoticeDraft,
    createEditNoticeDraftKey,
    createNoticeDraftPayload,
    readNoticeDraft,
    saveNoticeDraft,
} from './noticeDraftUtils'

const PANEL_SX = FORM_PANEL_SX
const getChoiceCardSx = getFormChoiceCardSx

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

function NoticeStatusSetting({ value, disabled, onChange }) {
    const options = [
        {
            value: 0,
            title: '미노출',
            description: '관리자에서만 편집하고 사용자 화면에는 보여주지 않습니다.',
        },
        {
            value: 1,
            title: '노출',
            description: '목록과 상세 화면에 공지사항을 즉시 표시합니다.',
        },
    ]

    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                상태
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio checked={checked} onChange={() => onChange(option.value)} disabled={disabled} />}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

export default function Notice(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.noticeId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText
    const searchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const draftKey = createEditNoticeDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState({})
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/notice/getNotice', { noticeId: id })

                if (data.code === 1) {
                    const baseData = data.data || {}
                    const storedDraft = readNoticeDraft(draftKey)
                    const restoredDraft = applyNoticeDraft({
                        baseData,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createNoticeDraftPayload({
                        data: baseData,
                    }))

                    setData(restoredDraft.data)
                    setHasDetail(Boolean(baseData?.noticeId))
                    setDetailErrorMessage(baseData?.noticeId ? '' : '공지사항 상세 정보를 찾을 수 없습니다.')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '공지사항 상세를 불러오지 못했습니다.'
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '공지사항 상세를 불러오지 못했습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 공지사항 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createNoticeDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearNoticeDraft(draftKey)
            return
        }

        saveNoticeDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady, hasDetail])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="공지사항 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/notices" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={listCurPage} onBackClick={() => clearNoticeDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    // 저장 후에도 운영자가 보던 공지 목록 조건으로 그대로 복귀시킨다.
    const moveToList = (snackbarState) => {
        clearNoticeDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/notices',
            search: listSearch,
            curPage: listCurPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const handleStatusChange = (status) => {
        setData((prev) => ({
            ...prev,
            status,
        }))
    }

    const updateData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/notice/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                moveToList({ severity: 'success', message: '공지사항을 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('공지사항을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (data.title || '').trim()
        const nextContent = (data.content || '').trim()

        setData((prev) => ({
            ...prev,
            title: nextTitle,
            content: nextContent
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('제목을 입력하세요.')
            return
        }

        const formData = new FormData()
        formData.append('noticeId', data.noticeId)
        formData.append('image', data.image)
        formData.append('title', nextTitle)
        formData.append('titleSub', data.titleSub)
        formData.append('content', nextContent)
        formData.append('url', data.url)
        formData.append('status', data.status)
        formData.append('order', data.order)

        updateData(formData)
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>공지사항 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>공지사항 수정</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="공지 내용"
                                description="일반 공지사항 목록과 상세에서 보여줄 기본 문구를 정리합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="제목"
                                        required
                                        id="title"
                                        name="title"
                                        value={data.title || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        id="content"
                                        name="content"
                                        multiline
                                        rows={15}
                                        value={data.content || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormSection
                                title="노출 설정"
                                description="공지사항을 운영 목록과 사용자 화면에 노출할지 마지막으로 확인합니다."
                            >
                                <NoticeStatusSetting
                                    value={data.status}
                                    disabled={saving}
                                    onChange={handleStatusChange}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
